// /opt/dashboard/frontend/src/pages/Registrations.js
// 🧑‍💻 Regisztrációk — ki és mikor regisztrált, napi bontással
import React, { useCallback, useEffect, useState } from 'react';

const C = {
  bg: '#1a1d27', panel: '#22252f', panel2: '#2a2e3a', border: '#2a2e3a',
  text: '#e0e6ed', muted: '#8892a0', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', bad: '#ef4444',
};

const PERIODS = [7, 30, 90, 365];

const fmtDT = (ms) => {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleString('hu-HU', {
      timeZone: 'Europe/Budapest',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return '—'; }
};
const platIcon = (p) => (p === 'android' ? '🤖 Android' : p === 'ios' ? '🍎 iOS' : '❓ ismeretlen');

function EmailChip({ email }) {
  const [copied, setCopied] = useState(false);
  if (!email) return <span style={{ color: C.muted }}>—</span>;
  return (
    <span
      onClick={() => {
        navigator.clipboard.writeText(email).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }).catch(() => {});
      }}
      title="Kattints a másoláshoz"
      style={{
        cursor: 'pointer', padding: '2px 8px', borderRadius: 6, fontSize: 12,
        background: copied ? 'rgba(34,197,94,0.15)' : C.panel2,
        border: `1px solid ${copied ? C.ok : C.border}`,
        color: copied ? C.ok : C.text,
      }}
    >
      {copied ? '✅ másolva' : email}
    </span>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div style={{
      background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: '14px 18px', flex: '1 1 140px', minWidth: 140,
    }}>
      <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || C.text, marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function Registrations() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback((d) => {
    setLoading(true);
    setError(null);
    fetch(`/api/registrations?days=${d}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((j) => { setData(j); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const maxDaily = data && data.daily.length > 0 ? Math.max(...data.daily.map((r) => r.count)) : 1;

  return (
    <div style={{ color: C.text, maxWidth: 1000 }}>
      <h2 style={{ margin: '0 0 4px' }}>🧑‍💻 Regisztrációk</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
        Ki és mikor regisztrált — Europe/Budapest idő szerint
      </div>

      {/* Időszak */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {PERIODS.map((p) => (
          <span
            key={p}
            onClick={() => setDays(p)}
            style={{
              padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, userSelect: 'none',
              background: days === p ? C.accent : C.panel,
              border: `1px solid ${days === p ? C.accent : C.border}`,
              color: days === p ? '#0a0e1a' : C.text,
              fontWeight: days === p ? 700 : 400,
            }}
          >
            {p} nap
          </span>
        ))}
      </div>

      {loading && <div style={{ color: C.muted, padding: 20 }}>⏳ Betöltés…</div>}
      {error && (
        <div style={{ background: C.panel, border: `1px solid ${C.bad}`, borderRadius: 12, padding: 16, color: C.bad }}>
          ❌ Hiba: {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* KPI-k */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Kpi label={`Összes (${data.days} nap)`} value={data.total} color={C.accent} />
            <Kpi label="🍎 iOS" value={data.platforms.ios || 0} />
            <Kpi label="🤖 Android" value={data.platforms.android || 0} />
            <Kpi label="❓ Ismeretlen" value={data.platforms.ismeretlen || 0} color={C.muted} />
            <Kpi label="💳 Vásárolt" value={data.purchasedCount} color={C.ok} />
          </div>

          {/* Napi bontás */}
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
              Napi regisztrációk
            </div>
            {data.daily.length === 0 && <div style={{ color: C.muted }}>Nincs adat az időszakban.</div>}
            {data.daily.map((r) => (
              <div key={r.date} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: C.muted, width: 82, flexShrink: 0 }}>{r.date}</span>
                <div style={{ flex: 1, height: 16, background: C.panel2, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${(r.count / maxDaily) * 100}%`,
                    background: `linear-gradient(90deg, ${C.accent}, #8b5cf6)`, borderRadius: 4,
                  }} />
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: 13, width: 34, textAlign: 'right', flexShrink: 0 }}>{r.count}</span>
              </div>
            ))}
          </div>

          {/* Lista */}
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
              Regisztrációk ({data.users.length}{data.truncated ? ' — első 500 megjelenítve' : ''})
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: C.muted, textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>Mikor</th>
                    <th style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>Email</th>
                    <th style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>Platform</th>
                    <th style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>Affiliate</th>
                    <th style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>Város</th>
                    <th style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>💳</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.uid}>
                      <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(u.createdAt)}</td>
                      <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}><EmailChip email={u.email} /></td>
                      <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{platIcon(u.platform)}</td>
                      <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>
                        {u.affiliateCode
                          ? <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, background: 'rgba(0,212,255,0.1)', border: `1px solid ${C.accent}`, color: C.accent }}>{u.affiliateCode}</span>
                          : <span style={{ color: C.muted }}>—</span>}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>{u.city || <span style={{ color: C.muted }}>—</span>}</td>
                      <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}` }}>{u.purchased ? '💳' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}
